import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useZxing } from 'react-zxing'; 
import { UserProfile } from '../../types';
import { api } from '../../services/api';
import { LogOut, ScanLine, Volume2, VolumeX, CheckCircle, Loader2, Wifi, WifiOff } from 'lucide-react';

// --- AUDIO ENGINE (ANDROID CHROME OPTIMIZED) ---
let audioCtx: AudioContext | null = null;

const getAudioContext = () => {
    if (typeof window === 'undefined') return null;
    if (!audioCtx) {
        const Ctor = window.AudioContext || (window as any).webkitAudioContext;
        if (Ctor) audioCtx = new Ctor();
    }
    return audioCtx;
};

const playTone = (freq: number, type: 'sine' | 'square' | 'sawtooth', duration: number) => {
    try {
        const ctx = getAudioContext();
        if (!ctx) return;
        if (ctx.state === 'suspended') ctx.resume();
        
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = type; 
        osc.frequency.setValueAtTime(freq, ctx.currentTime);
        gain.gain.setValueAtTime(0, ctx.currentTime);
        gain.gain.linearRampToValueAtTime(1.0, ctx.currentTime + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + duration);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start();
        osc.stop(ctx.currentTime + duration);
    } catch (e) {
        console.error("Audio failed", e);
    }
};

interface Props {
  packer: UserProfile;
  onLogout: () => void;
}

interface QueueItem {
  id: string;
  blob: Blob;
  awb: string;
  filename: string;
  mimeType: string;
  status: 'PENDING' | 'UPLOADING' | 'FAILED';
  retryCount: number;
  error?: string;
  addedAt: number;
}

const AndroidPackerInterface: React.FC<Props> = ({ packer, onLogout }) => {
  const [status, setStatus] = useState<'IDLE' | 'STABILIZING' | 'DETECTED' | 'RECORDING'>('IDLE');
  const [awb, setAwb] = useState(''); 
  const [uploadQueue, setUploadQueue] = useState<QueueItem[]>([]);
  const [activeUploads, setActiveUploads] = useState(0);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [totalUploaded, setTotalUploaded] = useState(0);
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [lastError, setLastError] = useState<string | null>(null);
  
  // ✅ NEW: Force scanner restart
  const [scannerKey, setScannerKey] = useState(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const awbRef = useRef(''); 
  const stableTimerRef = useRef<any>(null);
  
  const isRecordingRef = useRef(false);
  const isIntentionalStopRef = useRef(false);
  
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafIdRef = useRef<number | null>(null);
  const videoElementRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  
  // ✅ NEW: Track the camera stream separately
  const cameraStreamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    const handleOnline = () => {
      console.log("📡 Android: Online");
      setIsOnline(true);
      playTone(880, 'sine', 0.1);
    };
    const handleOffline = () => {
      console.log("📡 Android: Offline");
      setIsOnline(false);
      playTone(220, 'sawtooth', 0.3);
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const enableAudio = () => {
      const ctx = getAudioContext();
      if (ctx && ctx.state === 'suspended') {
          ctx.resume().then(() => console.log("🔊 Android: Audio unlocked"));
      }
      playTone(660, 'sine', 0.1);
      setAudioEnabled(true);
  };

  const addToQueue = useCallback((blob: Blob, recordedAwb: string, mimeType: string) => {
      const ext = mimeType.includes('mp4') ? 'mp4' : 'webm';
      const finalAwb = recordedAwb || `scan_${Date.now()}`; 
      const filename = `${finalAwb}.${ext}`;
      console.log("➕ Android: Queued", finalAwb, `(${(blob.size / 1024 / 1024).toFixed(2)} MB)`);
      setUploadQueue(prev => [...prev, {
          id: Date.now().toString(),
          blob,
          awb: finalAwb,
          filename,
          mimeType,
          status: 'PENDING',
          retryCount: 0,
          addedAt: Date.now()
      }]);
  }, []);

  const startCanvasLoop = (video: HTMLVideoElement, awbToDraw: string) => {
      // ✅ Cancel any existing loop first
      stopCanvasLoop();
      
      const canvas = canvasRef.current;
      if (!canvas || !video) return;

      const ctx = canvas.getContext('2d', {
          willReadFrequently: false,
          alpha: false
      });
      if (!ctx) return;

      const initCanvas = () => {
          // ✅ FIX: Force 640x480 canvas. 
          // Locking exact dimensions prevents encoder errors on budget phones.
          canvas.width = 640;
          canvas.height = 480;
          console.log(`🎨 Android Canvas: Forced 640x480`);
          startDrawLoop();
      };

      const startDrawLoop = () => {
          let lastDrawTime = 0;
          const frameInterval = 1000 / 20;

          const draw = (timestamp: number) => {
              if (timestamp - lastDrawTime < frameInterval) {
                  if (isRecordingRef.current) {
                      rafIdRef.current = requestAnimationFrame(draw);
                  }
                  return;
              }
              lastDrawTime = timestamp;

              if (!video.paused && !video.ended && isRecordingRef.current) {
                  try {
                      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

                      const now = new Date();
                      const fontSize = Math.max(12, Math.floor(canvas.height * 0.03)); 
                      ctx.font = `bold ${fontSize}px monospace`;
                      ctx.fillStyle = 'white';
                      ctx.textBaseline = 'top';
                      ctx.shadowColor = 'black';
                      ctx.shadowBlur = 4;
                      ctx.lineWidth = 3;
                      ctx.strokeStyle = 'black';

                      const lines = [
                          `AWB: ${awbToDraw}`,
                          `${now.toLocaleDateString()} ${now.toLocaleTimeString()}`
                      ];

                      let yPos = canvas.height - (lines.length * (fontSize + 10)) - 20;
                      lines.forEach((line) => {
                          ctx.strokeText(line, 20, yPos);
                          ctx.fillText(line, 20, yPos);
                          yPos += fontSize + 10;
                      });
                  } catch (e) {
                      console.error("Android: Canvas error", e);
                  }
              }
              
              if (isRecordingRef.current) {
                  rafIdRef.current = requestAnimationFrame(draw);
              }
          };
          rafIdRef.current = requestAnimationFrame(draw);
      };

      initCanvas();
  };

  const stopCanvasLoop = () => {
      if (rafIdRef.current) {
          cancelAnimationFrame(rafIdRef.current);
          rafIdRef.current = null;
      }
      const canvas = canvasRef.current;
      if (canvas) {
          const ctx = canvas.getContext('2d');
          if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
  };

  const tryCreateMediaRecorder = (stream: MediaStream): MediaRecorder | null => {
      // ✅ FIX: Prefer MP4 first (Hardware Accelerated on Android)
      const candidates = [
          'video/mp4',
          'video/webm;codecs=h264',
          'video/webm',
          '' // Empty string = Browser Default (Safest option)
      ];

      for (const mime of candidates) {
          try {
              if (mime && typeof MediaRecorder.isTypeSupported === 'function' && !MediaRecorder.isTypeSupported(mime)) {
                  continue;
              }
              
              // ✅ FIX: Do NOT set videoBitsPerSecond. 
              // Budget phones crash if you force a bitrate they can't handle.
              const options: any = mime ? { mimeType: mime } : {};
              
              const mr = new MediaRecorder(stream, options);
              console.log(`✅ Android: Created recorder with ${mr.mimeType || 'default settings'}`);
              return mr;
          } catch (err) {
              console.warn(`⚠️ Android: Failed to create recorder for ${mime}`, err);
          }
      }
      return null;
  };

  const triggerRecordStart = useCallback((videoElement: HTMLVideoElement, awbToUse: string) => {
      if (isRecordingRef.current) {
          console.warn("⚠️ Android: Already recording");
          return;
      }
      if (!videoElement || !videoElement.srcObject || !awbToUse) return;

      // ✅ Store the camera stream before we start recording
      cameraStreamRef.current = videoElement.srcObject as MediaStream;

      isIntentionalStopRef.current = false;
      isRecordingRef.current = true;
      console.log(`🎬 Android: Recording ${awbToUse}`);

      startCanvasLoop(videoElement, awbToUse);

      setTimeout(() => {
          // ✅ Check if still valid
          if (!isRecordingRef.current || !canvasRef.current) {
              console.log("🚫 Android: Recording cancelled during startup");
              return;
          }
          
          try {
              const canvas = canvasRef.current;
              if (!canvas) throw new Error("No canvas");

              const stream = canvas.captureStream(20);
              streamRef.current = stream;

              if (!stream.active) throw new Error("Stream not active");

              const mediaRecorder = tryCreateMediaRecorder(stream);
              if (!mediaRecorder) {
                  alert("Recording not supported on this device");
                  stopCanvasLoop();
                  isRecordingRef.current = false;
                  setStatus('IDLE');
                  return;
              }

              mediaRecorderRef.current = mediaRecorder;
              chunksRef.current = [];

              mediaRecorder.ondataavailable = (e) => {
                  if (e.data && e.data.size > 0) {
                      chunksRef.current.push(e.data);
                      console.log(`📦 Android: Chunk ${(e.data.size / 1024).toFixed(1)}KB (${chunksRef.current.length})`);
                  }
              };

              mediaRecorder.onerror = (e: any) => {
                  console.error("❌ Android: Recorder error", e);
                  setLastError("Recording error occurred");
              };

              mediaRecorder.onstop = () => {
                  console.log("🛑 Android: Stopped");
                  
                  if (isIntentionalStopRef.current) {
                      if (chunksRef.current.length > 0) {
                          const blob = new Blob(chunksRef.current, { type: (mediaRecorder as any).mimeType || 'video/webm' });
                          if (blob.size > 1000) {
                              console.log(`✅ Android: Saved ${(blob.size / 1024 / 1024).toFixed(2)}MB`);
                              addToQueue(blob, awbToUse, (mediaRecorder as any).mimeType || 'video/webm');
                              playTone(880, 'sine', 0.2);
                          } else {
                              console.warn("⚠️ Android: Recording too small");
                              setLastError("Recording was too small");
                          }
                      } else {
                          console.error("❌ Android: No chunks");
                          setLastError("Recording failed: No data");
                      }
                  } else {
                      console.error("❌ Android: SYSTEM STOP - Crash detected");
                      setLastError("Recording interrupted by system");
                  }

                  stopCanvasLoop();
                  isRecordingRef.current = false;
                  
                  // ✅ Stop only the canvas stream (not camera)
                  if (streamRef.current) {
                      streamRef.current.getTracks().forEach(t => {
                          t.stop();
                          console.log(`🔌 Android: Stopped canvas track ${t.label}`);
                      });
                      streamRef.current = null;
                  }

                  // ✅ CRITICAL FIX: Restart the scanner
                  console.log("🔄 Android: Restarting scanner...");
                  setStatus('IDLE');
                  
                  // Force useZxing to reinitialize by changing key
                  setTimeout(() => {
                      setScannerKey(prev => prev + 1);
                      console.log("✅ Android: Scanner restarted");
                  }, 100);
              };

              mediaRecorder.start(1000);
              console.log("🎥 Android: Started (1000ms chunks)");
              setStatus('RECORDING');
          } catch (e: any) {
              console.error("❌ Android: Recording failed", e);
              setLastError(`Failed: ${e.message}`);
              stopCanvasLoop();
              isRecordingRef.current = false;
              setStatus('IDLE');
          }
      }, 100);
  }, [addToQueue]);

  const stopRecording = useCallback(() => {
      console.log("🛑 Android: User stop");
      playTone(150, 'sawtooth', 0.3);
      
      isIntentionalStopRef.current = true;
      
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
          try {
              mediaRecorderRef.current.stop();
              console.log("✅ Android: Stop called");
          } catch (e) {
              console.error("❌ Android: Stop error", e);
              isRecordingRef.current = false;
              stopCanvasLoop();
              setStatus('IDLE');
              // ✅ Restart scanner on error too
              setTimeout(() => setScannerKey(prev => prev + 1), 100);
          }
      } else {
          console.warn("⚠️ Android: Not recording");
          isRecordingRef.current = false;
          setStatus('IDLE');
      }

      setAwb('');
      awbRef.current = '';
      if (stableTimerRef.current) {
          clearTimeout(stableTimerRef.current);
          stableTimerRef.current = null;
      }
      setStatus('IDLE'); 
  }, []);

  const confirmScan = useCallback((code: string, videoElement: HTMLVideoElement) => {
      let cleanCode = code.trim();
      if (cleanCode.length > 8 && cleanCode.length % 2 === 0) {
        const half = cleanCode.length / 2;
        if (cleanCode.slice(0, half) === cleanCode.slice(half)) {
            cleanCode = cleanCode.slice(0, half);
            console.log("🔄 Android: Deduped", cleanCode);
        }
      }
      console.log("✅ Android: Scanned", cleanCode);
      setAwb(cleanCode);
      awbRef.current = cleanCode;
      playTone(880, 'square', 0.2);
      setStatus('DETECTED');
      try { if (navigator.vibrate) navigator.vibrate(200); } catch (e) {}
      setTimeout(() => triggerRecordStart(videoElement, cleanCode), 500); 
  }, [triggerRecordStart]);

  const onScanResult = useCallback((result: any) => {
      // If we are already recording or processing, ignore new scans
      if (status !== 'IDLE' && status !== 'STABILIZING') return;

      const rawCode = result.getText();
      if (!rawCode || !rawCode.trim()) return;

      // ✅ INSTANT FIX: Remove the 800ms wait. 
      // Instead, we just debounce slightly (200ms) to prevent ghost scans
      if (stableTimerRef.current) clearTimeout(stableTimerRef.current);

      setStatus('STABILIZING'); // Show visual feedback immediately

      stableTimerRef.current = setTimeout(() => {
          const videoEl = videoElementRef.current;
          if (videoEl && status !== 'RECORDING') {
              confirmScan(rawCode, videoEl);
          }
          stableTimerRef.current = null;
      }, 200); // ⚡ Reduced from 800ms to 200ms for speed
  }, [status, confirmScan]);

  // 1. Define constraints with useMemo (STOPS THE CRASH)
  const constraints = useMemo(() => ({
    audio: false,
    video: { 
        facingMode: 'environment',
        width: { ideal: 640 }, 
        height: { ideal: 480 },
        frameRate: { ideal: 15 } // Critical: 15 FPS reduces CPU load by 50%
    }
  }), []);

  // 2. Pass the stable variable to the hook
  const { ref: zxingRef } = useZxing({
    key: scannerKey,
    onDecodeResult: onScanResult,
    onError: (error) => {
        console.error("❌ ZXing Error:", error);
        setLastError(`Cam Error: ${error.name} - ${error.message}`); 
    },
    // Keep scanning while stabilizing; only pause once we are actually recording
    paused: status === 'RECORDING' || status === 'DETECTED',
    timeBetweenDecodingAttempts: 300,
    constraints: constraints // ✅ Uses the memoized variable
  });

  const videoRefCallback = useCallback((node: HTMLVideoElement | null) => {
    if (node) {
      videoElementRef.current = node;
      zxingRef(node);
      console.log("📹 Android: Video ref set", scannerKey);
    }
  }, [zxingRef, scannerKey]); // ✅ Add scannerKey to deps

  // Upload logic
  useEffect(() => {
      if (!isOnline) return;
      const pendingItems = uploadQueue.filter(i => i.status === 'PENDING' || i.status === 'FAILED');
      if (pendingItems.length > 0 && activeUploads < 1) {
          const item = pendingItems[0];
          if (item.retryCount >= 3) {
              console.error("❌ Android: Max retries", item.awb);
              setUploadQueue(prev => prev.filter(i => i.id !== item.id));
              return;
          }
          setUploadQueue(prev => prev.map(i => i.id === item.id ? { ...i, status: 'UPLOADING', retryCount: i.retryCount + 1 } : i));
          setActiveUploads(prev => prev + 1);

          (async () => {
              try {
                  console.log(`⬆️ Android: Uploading ${item.awb}`);
                  const tokenRes = await api.getUploadToken(item.filename, item.mimeType);
                  const metadata = tokenRes.metadata ? JSON.parse(tokenRes.metadata) : {
                      name: item.filename,
                      mimeType: item.mimeType,
                      parents: [tokenRes.folderId]
                  };
                  const createRes = await fetch('https://www.googleapis.com/drive/v3/files', {
                      method: 'POST',
                      headers: { 'Authorization': `Bearer ${tokenRes.accessToken}`, 'Content-Type': 'application/json' },
                      body: JSON.stringify(metadata)
                  });
                  if (!createRes.ok) throw new Error("Create failed");
                  const fileData = await createRes.json();
                  const uploadRes = await fetch(
                      `https://www.googleapis.com/upload/drive/v3/files/${fileData.id}?uploadType=media`,
                      {
                          method: 'PATCH',
                          headers: { 'Authorization': `Bearer ${tokenRes.accessToken}`, 'Content-Type': item.mimeType },
                          body: item.blob
                      }
                  );
                  if (!uploadRes.ok) throw new Error("Upload failed");
                  await api.completeFulfillment({
                      stage: 1,
                      awb: item.awb,
                      videoUrl: `https://drive.google.com/file/d/${fileData.id}/view`,
                      folder_id: tokenRes.folderId
                  });
                  console.log(`🎉 Android: Success ${item.awb}`);
                  setUploadQueue(prev => prev.filter(i => i.id !== item.id));
                  setTotalUploaded(prev => prev + 1);
                  playTone(660, 'sine', 0.15);
              } catch (e: any) {
                  console.error(`❌ Android: Upload failed`, e);
                  setUploadQueue(prev => prev.map(i => i.id === item.id ? { ...i, status: 'FAILED', error: e.message } : i));
              } finally {
                  setActiveUploads(prev => prev - 1);
              }
          })();
      }
  }, [uploadQueue, activeUploads, isOnline]);

  useEffect(() => {
      return () => {
          console.log("🧹 Android: Cleanup");
          if (stableTimerRef.current) clearTimeout(stableTimerRef.current);
          if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
              try { mediaRecorderRef.current.stop(); } catch (e) {}
          }
          stopCanvasLoop();
          if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
          if (cameraStreamRef.current) cameraStreamRef.current.getTracks().forEach(t => t.stop());
          isRecordingRef.current = false;
      };
  }, []);

  const pendingCount = uploadQueue.filter(i => i.status === 'PENDING').length;

  return (
    <div className="fixed inset-0 bg-black overflow-hidden flex flex-col" onClick={() => !audioEnabled && enableAudio()}>
        <canvas ref={canvasRef} className="hidden absolute pointer-events-none" />

        <div className="absolute top-0 left-0 right-0 z-20 p-4 flex justify-between items-start bg-gradient-to-b from-black/90 to-transparent">
            <div>
                <h1 className="text-white font-bold text-lg drop-shadow-md">{packer.name}</h1>
                <div className="flex flex-col gap-1 text-xs text-white/80">
                   {uploadQueue.length === 0 ? (
                       <span className="flex items-center gap-1 text-green-400 font-bold">
                           <CheckCircle size={12}/> Ready • {totalUploaded} uploaded
                       </span>
                   ) : (
                       <span className="flex items-center gap-1 text-yellow-400 font-bold">
                           <Loader2 size={12} className="animate-spin"/> 
                           {activeUploads} uploading • {pendingCount} pending
                       </span>
                   )}
                   <div className={`flex items-center gap-1 text-xs ${isOnline ? 'text-green-400' : 'text-red-400'}`}>
                       {isOnline ? <Wifi size={10}/> : <WifiOff size={10}/>}
                       <span>{isOnline ? 'Online' : 'Offline'}</span>
                   </div>
                   <span className="text-xs text-blue-400">Android Chrome • Scan #{scannerKey}</span>
                </div>
            </div>
            <div className="flex gap-3">
                 <div className={`p-2 rounded-full backdrop-blur ${audioEnabled ? 'bg-white/10 text-white' : 'bg-red-500/50 text-white animate-pulse'}`}>
                    {audioEnabled ? <Volume2 size={18} /> : <VolumeX size={18} />}
                 </div>
                 <button onClick={onLogout} className="p-2 bg-white/10 rounded-full text-white backdrop-blur hover:bg-white/20 transition-colors">
                    <LogOut size={18} />
                 </button>
            </div>
        </div>

        <video 
            key={scannerKey} // ✅ Force video recreation too
            ref={videoRefCallback}
            className="absolute inset-0 w-full h-full object-cover"
            playsInline muted autoPlay
        />

        {status === 'STABILIZING' && (
             <div className="absolute inset-0 pointer-events-none z-10 flex flex-col items-center justify-center bg-black/20">
                 <div className="absolute inset-4 border-4 border-yellow-400/50 rounded-2xl animate-pulse"></div>
                 <ScanLine className="text-yellow-400 animate-pulse w-32 h-32 drop-shadow-lg" />
                 <p className="text-yellow-400 font-black text-2xl mt-4 drop-shadow-md">HOLD STEADY...</p>
             </div>
        )}

        {status === 'DETECTED' && (
            <div className="absolute inset-0 z-30 flex items-center justify-center bg-green-500/20 backdrop-blur-sm pointer-events-none">
                <div className="bg-green-600 text-white px-10 py-8 rounded-3xl shadow-2xl animate-bounce">
                    <h2 className="text-4xl font-black tracking-tighter">SCANNED!</h2>
                    <p className="text-center font-mono text-xl mt-2">{awb}</p>
                </div>
            </div>
        )}

        {status === 'RECORDING' && (
            <div onClick={(e) => { e.stopPropagation(); stopRecording(); }}
                className="absolute bottom-0 left-0 w-full h-[70%] bg-red-600/90 z-40 flex flex-col items-center justify-center backdrop-blur-md active:bg-red-700 transition-colors cursor-pointer touch-manipulation">
                <div className="bg-white/20 p-8 rounded-full animate-pulse mb-6">
                    <div className="w-8 h-8 bg-white rounded-sm"></div>
                </div>
                <h2 className="text-white font-black text-6xl tracking-widest drop-shadow-xl select-none mb-2">STOP</h2>
                <p className="text-white/90 text-sm mb-4">TAP TO STOP RECORDING</p>
                <p className="text-white/80 font-mono text-xl mt-3 px-4 py-2 bg-black/20 rounded-lg">{awb}</p>
            </div>
        )}

        {status === 'IDLE' && (
            <div className="absolute inset-0 pointer-events-none z-10 flex flex-col items-center justify-center">
                 {!audioEnabled && (
                     <div className="absolute top-24 bg-red-600 text-white px-6 py-3 rounded-full font-bold animate-bounce z-50 shadow-lg pointer-events-auto cursor-pointer">
                        TAP SCREEN TO ENABLE AUDIO
                     </div>
                 )}
                 <div className="bg-black/50 backdrop-blur-md px-6 py-3 rounded-full border border-white/30 flex items-center gap-3 mb-8 shadow-xl">
                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-white font-bold tracking-widest text-sm">SCANNER READY</span>
                 </div>
            </div>
        )}

        {uploadQueue.length > 0 && status === 'IDLE' && (
            <div className="absolute bottom-4 left-4 right-4 bg-black/70 backdrop-blur-md rounded-2xl p-4 z-20">
                <div className="flex items-center justify-between text-white text-sm">
                    <div className="flex items-center gap-2">
                        <Loader2 size={16} className="animate-spin text-yellow-400"/>
                        <span className="font-semibold">Upload Queue</span>
                    </div>
                    <span className="text-white/70">{uploadQueue.length} items</span>
                </div>
            </div>
        )}

        {lastError && status === 'IDLE' && (
            <div className="absolute top-1/2 left-4 right-4 transform -translate-y-1/2 bg-red-600 text-white rounded-2xl p-4 z-30">
                <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                        <h3 className="font-bold mb-1">Error</h3>
                        <p className="text-sm">{lastError}</p>
                    </div>
                    <button onClick={() => setLastError(null)} className="px-3 py-1 bg-white/20 rounded-lg text-xs">
                        Dismiss
                    </button>
                </div>
            </div>
        )}
    </div>
  );
};

export default AndroidPackerInterface;
